import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Button, Spinner, Label, TextInput, Badge } from 'flowbite-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { HiX, HiChevronDown, HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { useUserContext } from 'src/context/UserProvider';
import { useToast } from 'src/context/ToastProvider';
import { createSupportTicket } from 'src/apis/support';
import { useMutation, useQueryClient } from 'react-query';
import { SupportQuestionCategory, UISupportQuestion, VoteType } from 'types';
import Select from 'react-select';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useGetSupportQuestions, useGetTicketById } from 'src/hooks/support.hooks';
import { useGetDropdownItemsByCategory } from 'src/hooks/dropdownMenu.hooks';
import { useVoteOnQuestion, useGetVotesByQuestion, useGetUserVote } from 'src/hooks/support.hooks';
import SupportTicketChat from './SupportTicketChat';
import TicketCreateStepForm from './TicketCreateStepForm';
import SupportModalQuestionsAccordion from './SupportModalQuestionsAccordion';
import { getSupportQuillModules } from './supportQuillConfig';

type ModalVersion = 'fallback' | 'search' | 'questions';

const stripHtml = (html: string): string => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
};

const slugToTitle = (slug: string): string =>
    slug
        ? slug.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
        : '';

// Quill toolbar for fallback / describe-issue (reused across modal) – includes image upload stored in S3, visible to AM/Admin
const fallbackQuillModules = getSupportQuillModules();

interface SupportModalProps {
    show: boolean;
    onClose: () => void;
    prefillData?: {
        category?: string;
        subcategory?: string;
        questionType?: SupportQuestionCategory;
        batchId?: number;
    };
}

const SupportModal: React.FC<SupportModalProps> = ({ show, onClose, prefillData }) => {
    const { user } = useUserContext();
    const toast = useToast();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [issueDescription, setIssueDescription] = useState('');
    const [isMinimized, setIsMinimized] = useState(false);
    // Version 1 (fallback) state: question type + batch for ticket
    const [fallbackQuestionType, setFallbackQuestionType] = useState<SupportQuestionCategory>(SupportQuestionCategory.GENERAL);
    const [fallbackBatchId, setFallbackBatchId] = useState<number>(0);
    // Version 2 (search) state
    const [searchQuery, setSearchQuery] = useState('');
    // Fallback conversation: after first send we keep modal open and show thread
    const [fallbackTicketId, setFallbackTicketId] = useState<string | null>(null);
    const lastSubmitWasFallbackRef = useRef(false);

    // URL as source of truth: use query params when present so modal content is always in sync
    const urlCategory = searchParams.get('category');
    const urlSubcategory = searchParams.get('subcategory');
    const urlBatchId = searchParams.get('batchId');
    const modalParam = searchParams.get('modal');
    const urlQuestionType = searchParams.get('questionType');
    const urlApplicationId = searchParams.get('applicationId');
    const urlCompanyId = searchParams.get('companyId');
    const urlPositionId = searchParams.get('positionId');
    const urlStep = searchParams.get('step');
    const urlQuestionId = searchParams.get('questionId');
    const urlTicketId = searchParams.get('ticketId');
    const isTicketCreateStep = urlStep === 'ticketCreate';
    const isTicketDetailsStep = urlStep === 'ticketdetails';
    // Show ticket chat when URL has ticketId (step=ticketdetails) or we just created a ticket (fallbackTicketId)
    const effectiveTicketId = urlTicketId || fallbackTicketId;
    const { data: ticketData } = useGetTicketById(effectiveTicketId || '');
    const ticketForHeader = (ticketData as any)?.data;
    const isCompanyMode = urlQuestionType === 'company';
    const isFallbackNoPocMapped = urlCategory === 'fallback-no-poc-mapped' && urlSubcategory === 'General';
    const effectivePrefill = useMemo(() => {
        if (prefillData?.category && prefillData?.subcategory) return prefillData;
        if (urlCategory && urlSubcategory && !isFallbackNoPocMapped) {
            const batchId = urlBatchId !== null && urlBatchId !== '' ? Number(urlBatchId) : 0;
            return {
                category: urlCategory,
                subcategory: urlSubcategory,
                questionType: isCompanyMode ? SupportQuestionCategory.COMPANY : SupportQuestionCategory.GENERAL,
                batchId: Number.isNaN(batchId) ? 0 : batchId,
            };
        }
        return prefillData;
    }, [prefillData, urlCategory, urlSubcategory, urlBatchId, isCompanyMode, isFallbackNoPocMapped]);

    // Determine modal version: questions (3) | search (2) | fallback (1)
    // fallback-no-poc-mapped + General => fallback form with default category/subcategory in URL
    const version: ModalVersion = useMemo(() => {
        if (effectivePrefill?.category && effectivePrefill?.subcategory) return 'questions';
        if (modalParam === 'search' && !urlCategory && !urlSubcategory) return 'search';
        return 'fallback';
    }, [effectivePrefill?.category, effectivePrefill?.subcategory, urlCategory, urlSubcategory, modalParam]);

    const clearTicketCreateStep = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('step');
        next.delete('questionId');
        navigate(`${location.pathname}?${next.toString()}`, { replace: true });
    }, [searchParams, navigate, location.pathname]);

    const clearTicketDetailsStep = useCallback(() => {
        setFallbackTicketId(null);
        const next = new URLSearchParams(searchParams);
        next.delete('step');
        next.delete('ticketId');
        navigate(`${location.pathname}?${next.toString()}`, { replace: true });
    }, [searchParams, navigate, location.pathname]);

    const goToTicketDetails = useCallback((ticketId: string) => {
        setFallbackTicketId(ticketId);
        const next = new URLSearchParams(searchParams);
        next.set('step', 'ticketdetails');
        next.set('ticketId', ticketId);
        navigate(`${location.pathname}?${next.toString()}`, { replace: true });
    }, [searchParams, navigate, location.pathname]);

    const prevShowRef = useRef(show);
    // Reset form state only when modal actually closes (show goes true -> false). Prevents running on URL-only updates (e.g. step=ticketCreate) which would cause blink.
    useEffect(() => {
        if (prevShowRef.current && !show) {
            setIssueDescription('');
            setSearchQuery('');
            setFallbackTicketId(null);
            setIsMinimized(false);
            const next = new URLSearchParams(searchParams);
            next.delete('step');
            next.delete('questionId');
            next.delete('ticketId');
            navigate(`${location.pathname}?${next.toString()}`, { replace: true });
        }
        prevShowRef.current = show;
    }, [show, clearTicketCreateStep]);

    // When opening fallback with batchId in URL (e.g. batch with no questions), prefill batch
    useEffect(() => {
        if (version === 'fallback' && urlBatchId !== null && urlBatchId !== '') {
            const num = Number(urlBatchId);
            if (!Number.isNaN(num)) setFallbackBatchId(num);
        }
    }, [version, urlBatchId]);

    // Batch options for Version 1 (fallback): Placement (0) + user's batches
    const batchOptionsForFallback = useMemo(() => {
        const options: Array<{ label: string; value: number }> = [
            { label: 'Placement', value: 0 },
        ];
        if (user?.lmsBatchDetails && Array.isArray(user.lmsBatchDetails)) {
            (user.lmsBatchDetails as any[]).forEach((bd: any) => {
                if (bd.batchId !== 0) {
                    options.push({ label: bd.batchName || `Batch ${bd.batchId}`, value: bd.batchId });
                }
            });
        }
        return options;
    }, [user?.lmsBatchDetails]);

    // Fetch dropdown menu items to map category/subcategory names to values
    const { data: categoriesData } = useGetDropdownItemsByCategory('SUPPORT_TICKET_CATEGORY', undefined, true, true);
    const { data: subcategoriesData } = useGetDropdownItemsByCategory('SUPPORT_TICKET_SUB_CATEGORY', undefined, true, true);
    const categories = useMemo(() => categoriesData?.lmsTechStacks || [], [categoriesData]);
    const subcategories = useMemo(() => subcategoriesData?.lmsTechStacks || [], [subcategoriesData]);

    // Find category and subcategory items to get their display names and values
    const categoryItem = categories.find(
        (cat: any) => (cat.name === effectivePrefill?.category || cat.label === effectivePrefill?.category) && cat.isActive
    );
    const categoryDisplayName = categoryItem?.label || categoryItem?.name || effectivePrefill?.category || '';

    const subcategoryItem = subcategories.find(
        (sub: any) => (sub.name === effectivePrefill?.subcategory || sub.label === effectivePrefill?.subcategory) && sub.isActive
    );
    const subcategoryValue = subcategoryItem?.name || effectivePrefill?.subcategory || '';
    const subcategoryDisplayName = subcategoryItem?.label || subcategoryItem?.name || effectivePrefill?.subcategory || '';

    // Fetch questions for current batch (for Version 2 search and Version 3)
    // When questionType=company (from application card), filter by COMPANY category and batchId=0
    const searchModeBatchId = version === 'search' && urlBatchId ? Number(urlBatchId) : undefined;
    const questionsBatchId = effectivePrefill?.batchId ?? searchModeBatchId ?? 0;
    const questionsCategory = isCompanyMode ? SupportQuestionCategory.COMPANY : undefined;
    const { data: questionsData, isLoading: isLoadingQuestions } = useGetSupportQuestions(
        version === 'fallback' ? undefined : (questionsCategory ?? undefined),
        undefined,
        undefined,
        version === 'fallback' ? undefined : questionsBatchId,
    );

    // Filter questions by subcategory (Version 3)
    const questions: UISupportQuestion[] = useMemo(() => {
        if (!questionsData?.data || !subcategoryValue) return [];

        let filtered = questionsData.data;

        // Filter by batchId
        if (effectivePrefill?.batchId !== undefined) {
            filtered = filtered.filter((q: any) => q.batchId === effectivePrefill.batchId);
        }

        // Filter by subcategory value (which is stored in questions.supportCategory)
        filtered = filtered.filter((q: any) => q.supportCategory === subcategoryValue);

        return filtered;
    }, [questionsData, effectivePrefill?.batchId, subcategoryValue]);

    // Version 2: all questions for batch, filtered by search query; with category/subcategory labels for display
    const searchBatchId = version === 'search' && urlBatchId !== null && urlBatchId !== '' ? Number(urlBatchId) : null;
    const searchableQuestions = useMemo(() => {
        if (version !== 'search' || !questionsData?.data || searchBatchId === null) return [];
        return questionsData.data.filter((q: any) => {
            const batchMatch = q.batchId === searchBatchId || (searchBatchId === 0 && (q.batchId === 0 || q.batchId == null));
            return batchMatch;
        });
    }, [version, questionsData?.data, searchBatchId]);
    const searchResults = useMemo(() => {
        const mapOne = (q: any) => {
            const subItem = subcategories.find((s: any) => s.name === q.supportCategory) as any;
            const parentName = subItem?.supportCategory;
            const catItem = categories.find((c: any) => c.name === parentName);
            const categoryName = catItem?.name ?? parentName ?? '';
            const subcategoryName = subItem?.name ?? q.supportCategory ?? '';
            const categoryLabel = catItem?.label || catItem?.name || parentName || '';
            const subcategoryLabel = subItem?.label || subItem?.name || q.supportCategory || '';
            return { question: q, categoryName, subcategoryName, categoryLabel, subcategoryLabel };
        };
        // Don't show any questions until the user has entered a search query
        if (!searchQuery.trim()) return [];
        const lower = searchQuery.toLowerCase().trim();
        const filtered = searchableQuestions.filter((q: any) =>
            (q.question && String(q.question).toLowerCase().includes(lower)) ||
            (q.answer && String(q.answer).toLowerCase().includes(lower))
        );
        return filtered.map(mapOne);
    }, [searchQuery, searchableQuestions, subcategories, categories]);

    const handleOpenQuestionDetail = (categoryName: string, subcategoryName: string) => {
        const next = new URLSearchParams(searchParams);
        next.set('category', categoryName);
        next.set('subcategory', subcategoryName);
        if (urlBatchId !== null && urlBatchId !== '') next.set('batchId', urlBatchId);
        if (isCompanyMode) next.set('questionType', 'company');
        if (urlApplicationId) next.set('applicationId', urlApplicationId);
        if (urlCompanyId) next.set('companyId', urlCompanyId);
        if (urlPositionId) next.set('positionId', urlPositionId);
        next.delete('modal');
        setSearchParams(next);
    };

    const handleBackToSearch = () => {
        const next = new URLSearchParams(searchParams);
        next.delete('category');
        next.delete('subcategory');
        next.set('modal', 'search');
        if (urlBatchId !== null && urlBatchId !== '') next.set('batchId', urlBatchId);
        if (isCompanyMode) next.set('questionType', 'company');
        if (urlApplicationId) next.set('applicationId', urlApplicationId);
        if (urlCompanyId) next.set('companyId', urlCompanyId);
        if (urlPositionId) next.set('positionId', urlPositionId);
        setSearchParams(next);
    };

    // Questions view: navigate to ticket-create step (image 3). Keeps category, subcategory, etc.; adds step=ticketCreate and optional questionId
    const goToTicketCreate = (questionId?: string) => {
        const next = new URLSearchParams(searchParams);
        next.set('step', 'ticketCreate');
        if (questionId) next.set('questionId', questionId);
        else next.delete('questionId');
        navigate(`${location.pathname}?${next.toString()}`, { replace: true });
    };

    const voteMutation = useVoteOnQuestion();

    const createTicketMutation = useMutation(['createSupportTicket'], createSupportTicket, {
        onSuccess(data: any) {
            queryClient.refetchQueries(['getMyTickets']);
            setIssueDescription('');
            clearTicketCreateStep();
            const ticketId = data?.data?._id != null ? String(data.data._id) : undefined;
            const wasFallback = lastSubmitWasFallbackRef.current;
            lastSubmitWasFallbackRef.current = false;
            // Navigate to ticket details (comments) so UI shows chat instead of questions
            if (ticketId) {
                queryClient.invalidateQueries(['getTicketById', ticketId]); // Force fresh fetch so all messages (including future admin replies) load
                goToTicketDetails(ticketId);
                setIsMinimized(false);
                toast({
                    title: wasFallback ? 'Message sent. Our team will get back to you shortly.' : 'Support ticket created successfully',
                    type: 'success',
                });
            } else {
                toast({
                    title: 'Support ticket created successfully',
                    type: 'success',
                });
                onClose();
            }
        },
        onError(error: any) {
            toast({
                title: error?.response?.data?.message || 'Failed to create ticket',
                type: 'error',
            });
        },
    });

    const handleVote = (questionId: string, question: string, voteType: VoteType) => {
        if (!user) {
            toast({
                title: 'Please login to vote',
                type: 'error',
            });
            return;
        }

        voteMutation.mutate({
            supportQuestionId: questionId,
            voteType,
            questionId: questionId,
            question,
            category: categoryDisplayName,
            subcategory: subcategoryDisplayName,
        });
    };

    const handleCreateTicketFromQuestion = (question: UISupportQuestion) => {
        if (!user) {
            toast({
                title: 'Please login to create a ticket',
                type: 'error',
            });
            return;
        }
        const hasContent = issueDescription.trim().length > 0 && stripHtml(issueDescription).trim().length > 0;
        if (!hasContent) {
            toast({
                title: 'Please describe your issue',
                type: 'error',
            });
            return;
        }

        const questionType = effectivePrefill?.questionType || SupportQuestionCategory.GENERAL;
        // Send full HTML (including embedded images) so it is stored and shown to AM/Admin
        createTicketMutation.mutate({
            questionType,
            questionId: question._id,
            subject: issueDescription,
            question: question.question.substring(0, 500),
            message: issueDescription,
            batchId: question.batchId !== undefined ? question.batchId : effectivePrefill?.batchId,
            ...(categoryItem?._id && { categoryId: categoryItem._id }),
            ...(subcategoryItem?._id && { subcategoryId: subcategoryItem._id }),
            ...(urlCompanyId && { companyId: urlCompanyId }),
            ...(urlPositionId && { positionId: urlPositionId }),
            ...(questionType === SupportQuestionCategory.COMPANY && urlApplicationId && { applicationId: urlApplicationId }),
        });
    };

    const handleSubmit = () => {
        const hasContent = issueDescription.trim().length > 0 && stripHtml(issueDescription).trim().length > 0;
        if (!hasContent) {
            toast({
                title: 'Please describe your issue',
                type: 'error',
            });
            return;
        }

        if (!user) {
            toast({
                title: 'Please login to create a ticket',
                type: 'error',
            });
            return;
        }

        const questionType = version === 'fallback' ? fallbackQuestionType : (isCompanyMode ? SupportQuestionCategory.COMPANY : (effectivePrefill?.questionType || SupportQuestionCategory.GENERAL));
        const batchId = version === 'fallback' ? fallbackBatchId : (isCompanyMode ? 0 : effectivePrefill?.batchId);
        const questionText = stripHtml(issueDescription).trim().substring(0, 500) || (effectivePrefill?.category && effectivePrefill?.subcategory
            ? `${effectivePrefill.category} - ${effectivePrefill.subcategory}`.substring(0, 500)
            : 'Support Request');

        if (version === 'fallback') lastSubmitWasFallbackRef.current = true;
        // subject: plain text for list/display (no <p> tags). message: full HTML for chat body.
        createTicketMutation.mutate({
            questionType,
            subject: stripHtml(issueDescription).trim() || questionText,
            question: questionText,
            message: issueDescription,
            batchId,
            ...(categoryItem?._id && { categoryId: categoryItem._id }),
            ...(subcategoryItem?._id && { subcategoryId: subcategoryItem._id }),
            ...(urlCompanyId && { companyId: urlCompanyId }),
            ...(urlPositionId && { positionId: urlPositionId }),
            ...(questionType === SupportQuestionCategory.COMPANY && urlApplicationId && { applicationId: urlApplicationId }),
        });
    };

    // Don't render if show is false
    if (!show) return null;

    if (version === 'questions' && (!effectivePrefill?.category || !effectivePrefill?.subcategory)) return null;

    return (
        <div className="fixed inset-0 z-[100] pointer-events-none">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black bg-opacity-20 pointer-events-auto"
                onClick={onClose}
            />

            {/* Modal positioned at bottom right - constrained to viewport */}
            <div
                className={`absolute bottom-6 right-6 w-[420px] max-w-[calc(100vw-3rem)] bg-white dark:bg-[#1F2A37] rounded-lg shadow-2xl pointer-events-auto transition-all duration-300 flex flex-col ${isMinimized ? 'h-16' : 'h-[600px] max-h-[calc(100vh-3rem)] min-h-0'
                    }`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                        {/* {(version === 'questions' || isFallbackNoPocMapped || effectiveTicketId) && (
                            <button
                                type="button"
                                onClick={effectiveTicketId ? clearTicketDetailsStep : (isFallbackNoPocMapped ? onClose : handleBackToSearch)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex-shrink-0"
                                aria-label="Back"
                            >
                                <HiChevronLeft className="w-5 h-5" />
                            </button>
                        )} */}
                        <div className="min-w-0">
                            {isFallbackNoPocMapped ? (
                                <>
                                    <h3 className="text-lg font-semibold dark:text-white truncate">Fallback No Poc Mapped</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">General</p>
                                </>
                            ) : effectiveTicketId ? (
                                <>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="text-lg font-semibold dark:text-white truncate">
                                            {ticketForHeader?.category?.label || ticketForHeader?.category?.name || (urlCategory && !/^[a-f0-9]{24}$/i.test(urlCategory) ? slugToTitle(urlCategory) : '') || ticketForHeader?.subject || 'Support'}
                                        </h3>
                                        {ticketForHeader?.status && (
                                            <Badge color={ticketForHeader.status === 'resolved' || ticketForHeader.status === 'closed' ? 'success' : 'info'} className="capitalize flex-shrink-0">
                                                {ticketForHeader.status === 'closed' ? 'Resolved' : ticketForHeader.status === 'escalated' || ticketForHeader.status === 'in_progress' ? 'Open' : ticketForHeader.status}
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                        {[
                                            ticketForHeader?.subcategory?.label || ticketForHeader?.subcategory?.name || (urlSubcategory && !/^[a-f0-9]{24}$/i.test(urlSubcategory) ? slugToTitle(urlSubcategory) : ''),
                                            ticketForHeader?.createdAt && new Date(ticketForHeader.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
                                        ].filter(Boolean).join(' • ')}
                                    </p>
                                </>
                            ) : version === 'questions' && categoryDisplayName ? (
                                <>
                                    <h3 className="text-lg font-semibold dark:text-white truncate">{categoryDisplayName}</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subcategoryDisplayName}</p>
                                </>
                            ) : version === 'search' ? (
                                <h3 className="text-lg font-semibold dark:text-white">Support</h3>
                            ) : (
                                <h3 className="text-lg font-semibold dark:text-white">Support</h3>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {/* <button
                            onClick={() => setIsMinimized(!isMinimized)}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            aria-label={isMinimized ? 'Maximize' : 'Minimize'}
                        >
                            <HiChevronDown className={`w-5 h-5 transition-transform ${isMinimized ? '' : 'rotate-180'}`} />
                        </button> */}
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            aria-label="Close"
                        >
                            <HiX className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content - Hidden when minimized; scrolls inside modal, no overflow */}
                {!isMinimized && (
                    <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden min-w-0">
                        {effectiveTicketId ? (
                            /* Ticket details / comments (step=ticketdetails&ticketId= or just created) */
                            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                                <SupportTicketChat
                                    ticketId={effectiveTicketId}
                                    onClose={clearTicketDetailsStep}
                                />
                            </div>
                        ) : version === 'questions' ? (
                            // Show questions if category and subcategory are provided
                            <div className="space-y-4 p-4 pt-2">
                                {isTicketCreateStep ? (
                                    <TicketCreateStepForm
                                        value={issueDescription}
                                        onChange={setIssueDescription}
                                        onSend={() => {
                                            const question = urlQuestionId && questions.length ? questions.find((q: UISupportQuestion) => q._id === urlQuestionId) : null;
                                            if (question) handleCreateTicketFromQuestion(question);
                                            else handleSubmit();
                                        }}
                                        isLoading={createTicketMutation.isLoading}
                                        onBack={clearTicketCreateStep}
                                        stripHtml={stripHtml}
                                    />
                                ) : isLoadingQuestions ? (
                                    <div className="flex justify-center py-8">
                                        <Spinner size="xl" />
                                    </div>
                                ) : questions.length === 0 ? (
                                    <div className="space-y-4">
                                        <p className="text-gray-600 dark:text-gray-400 text-center py-4">
                                            No questions found for this category and subcategory.
                                        </p>
                                        <Button
                                            color="purplePlaced"
                                            onClick={() => goToTicketCreate()}
                                            className="w-full"
                                        >
                                            Raise a Support Ticket
                                        </Button>
                                    </div>
                                ) : (
                                    <SupportModalQuestionsAccordion
                                        questions={questions}
                                        onRaiseTicket={goToTicketCreate}
                                        onVote={handleVote}
                                        voteLoading={voteMutation.isLoading}
                                    />
                                )}
                            </div>
                        ) : version === 'search' ? (
                            /* Version 2: Search questions by batch */
                            <div className="space-y-4 p-4 pt-2">
                                <Label htmlFor="support-search" className="text-sm font-medium dark:text-gray-300 block mb-2">Search questions</Label>
                                <div className="flex gap-2">
                                    <TextInput
                                        id="support-search"
                                        type="text"
                                        placeholder="Type to search..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="flex-1"
                                    />
                                    <Button color="purplePlaced" size="sm" className="flex-shrink-0" disabled>
                                        <HiChevronRight className="w-5 h-5" />
                                    </Button>
                                </div>
                                {isLoadingQuestions ? (
                                    <div className="flex justify-center py-6">
                                        <Spinner size="lg" />
                                    </div>
                                ) : (
                                    <div className="space-y-2 mt-6">
                                        {searchResults.length === 0 ? (
                                            <div className="space-y-6">
                                                <p className="text-gray-500 dark:text-gray-400 text-sm py-6 text-center">
                                                    {searchQuery.trim() ? 'No matching questions.' : (isCompanyMode ? 'No company-related questions found.' : 'Type above to search questions.')}
                                                </p>
                                                {isCompanyMode && (
                                                    <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                                            Have doubts about the company? Create a ticket and our support team will assist you shortly.
                                                        </p>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400 p-4 rounded-lg bg-gray-50 dark:bg-gray-800 mb-2">
                                                            Share the details of your issue so our support team can reach out with the right help.
                                                        </p>
                                                        <div className="support-fallback-quill min-w-0 mt-4">
                                                            <ReactQuill
                                                                theme="snow"
                                                                value={issueDescription}
                                                                onChange={setIssueDescription}
                                                                placeholder="Type message here"
                                                                modules={fallbackQuillModules}
                                                                className="bg-white dark:bg-gray-700 rounded-lg support-modal-quill [&_.ql-container]:h-[100px] [&_.ql-container]:min-h-[100px] [&_.ql-container]:overflow-y-auto [&_.ql-container]:resize-y [&_.ql-editor]:min-h-[98px]"
                                                            />
                                                        </div>
                                                        <div className="pt-2">
                                                            <Button
                                                                color="purplePlaced"
                                                                onClick={handleSubmit}
                                                                disabled={!stripHtml(issueDescription).trim() || createTicketMutation.isLoading}
                                                                className="w-full"
                                                            >
                                                                {createTicketMutation.isLoading ? (
                                                                    <>
                                                                        <Spinner size="sm" className="mr-2" />
                                                                        Creating...
                                                                    </>
                                                                ) : (
                                                                    'Create Ticket'
                                                                )}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            searchResults.map(({ question, categoryName, subcategoryName, categoryLabel, subcategoryLabel }: any) => (
                                                <button
                                                    key={question._id}
                                                    type="button"
                                                    onClick={() => handleOpenQuestionDetail(categoryName, subcategoryName)}
                                                    className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between gap-2"
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <p className="font-medium text-gray-900 dark:text-white truncate">{question.question}</p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                            {categoryLabel || categoryName} {categoryLabel || categoryName ? '→' : ''} {subcategoryLabel || subcategoryName}
                                                        </p>
                                                    </div>
                                                    <HiChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : isFallbackNoPocMapped ? (
                            /* Fallback No POC Mapped - default category/subcategory in URL, rich text like image 2 */
                            <div className="space-y-6 p-4 pt-2">
                                <p className="text-sm text-gray-600 dark:text-gray-400 p-4 rounded-lg bg-gray-50 dark:bg-gray-800 mb-2">
                                    Share the details of your issue so our support team can reach out with the right help.
                                </p>
                                <div className="support-fallback-quill support-modal-quill mt-4">
                                    <ReactQuill
                                        theme="snow"
                                        value={issueDescription}
                                        onChange={setIssueDescription}
                                        placeholder="Type message here."
                                        modules={fallbackQuillModules}
                                        className="bg-white dark:bg-gray-700 rounded-lg [&_.ql-container]:h-[120px] [&_.ql-container]:min-h-[120px] [&_.ql-container]:overflow-y-auto [&_.ql-container]:resize-y [&_.ql-editor]:min-h-[118px]"
                                    />
                                </div>
                                <div className="pt-2">
                                    <Button
                                        color="purplePlaced"
                                        onClick={handleSubmit}
                                        disabled={!stripHtml(issueDescription).trim() || createTicketMutation.isLoading}
                                        className="w-full"
                                    >
                                        {createTicketMutation.isLoading ? (
                                            <>
                                                <Spinner size="sm" className="mr-2" />
                                                Sending...
                                            </>
                                        ) : (
                                            'Send'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            /* Version 1a: Fallback - no category/questions; create ticket with question type + batch */
                            <div className="space-y-6 p-4 pt-2">
                                <div>
                                    <Label className="block text-sm font-medium mb-2 dark:text-gray-300">Question type</Label>
                                    <Select
                                        instanceId="question-type-select"
                                        options={[
                                            { label: 'General', value: SupportQuestionCategory.GENERAL },
                                            { label: 'Company', value: SupportQuestionCategory.COMPANY },
                                        ]}
                                        value={{ label: fallbackQuestionType === SupportQuestionCategory.COMPANY ? 'Company' : 'General', value: fallbackQuestionType }}
                                        onChange={(opt) => opt && setFallbackQuestionType(opt.value)}
                                        classNamePrefix="my-select"
                                        isSearchable={false}
                                    />
                                </div>
                                {fallbackQuestionType === SupportQuestionCategory.GENERAL && (
                                    <div>
                                        <Label className="block text-sm font-medium mb-2 dark:text-gray-300">Batch</Label>
                                        <Select
                                            instanceId="fallback-batch-select"
                                            options={batchOptionsForFallback}
                                            value={batchOptionsForFallback.find((b) => b.value === fallbackBatchId) || batchOptionsForFallback[0]}
                                            onChange={(opt) => opt && setFallbackBatchId(opt.value)}
                                            classNamePrefix="my-select"
                                            isSearchable={false}
                                        />
                                    </div>
                                )}
                                <p className="text-sm text-gray-600 dark:text-gray-400 p-4 rounded-lg bg-gray-50 dark:bg-gray-800 mb-2">
                                    Share the details of your issue so our support team can reach out with the right help.
                                </p>
                                <div className="support-fallback-quill min-w-0 support-modal-quill mt-4">
                                    <ReactQuill
                                        theme="snow"
                                        value={issueDescription}
                                        onChange={setIssueDescription}
                                        placeholder="Type message here"
                                        modules={fallbackQuillModules}
                                        className="bg-white dark:bg-gray-700 rounded-lg [&_.ql-container]:h-[120px] [&_.ql-container]:min-h-[120px] [&_.ql-container]:overflow-y-auto [&_.ql-container]:resize-y [&_.ql-editor]:min-h-[118px]"
                                    />
                                </div>
                                <div className="pt-2">
                                    <Button
                                        color="purplePlaced"
                                        onClick={handleSubmit}
                                        disabled={!stripHtml(issueDescription).trim() || createTicketMutation.isLoading}
                                        className="w-full"
                                    >
                                        {createTicketMutation.isLoading ? (
                                            <>
                                                <Spinner size="sm" className="mr-2" />
                                                Sending...
                                            </>
                                        ) : (
                                            'Send'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SupportModal;
